import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Share, PlusSquare, Lock } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { APPS, PRICE } from "@/lib/apps";
import { ease } from "@/lib/scroll";
import { Reveal, Eyebrow } from "@/components/temple/Reveal";
import BundleBand from "@/components/temple/BundleBand";

const AppCard = ({ app, i }) => {
  const { user } = useAuth();
  const owned = app.owns(user);
  const accent = app.key === "quantum" ? "#E8838E" : "#D9B47A";
  return (
    <Reveal delay={i * 0.12} className="h-full">
      <article data-testid={`apps-card-${app.key}`} className="group relative h-full flex flex-col border border-[#D9B47A]/20 p-7 sm:p-10 transition-colors duration-500 hover:border-[#D9B47A]/50" style={{ backgroundColor: app.bg }}>
        <div className="flex items-start justify-between gap-6">
          <img src={app.icon.replace("192", "512")} alt={`${app.name} app icon`} className="w-24 h-24 sm:w-28 sm:h-28 rounded-[22%] shadow-[0_20px_60px_rgba(0,0,0,0.6)] transition-transform duration-700 group-hover:-rotate-3 group-hover:scale-105" />
          <span className="font-tt-mono text-[12px] uppercase tracking-[0.25em] text-[#A8998C]">App {String(i + 1).padStart(2, "0")}</span>
        </div>
        <p className="mt-10 font-tt-mono text-[12px] uppercase tracking-[0.25em]" style={{ color: accent }}>{app.tagline}</p>
        <h2 data-testid={`apps-name-${app.key}`} className="mt-3 font-display text-4xl sm:text-5xl leading-[1.02]">{app.name}</h2>
        <p className="mt-5 text-[16px] leading-relaxed text-[#CFC2B6]">{app.blurb}</p>
        <ul className="mt-7 space-y-3">
          {app.includes.map((t) => (
            <li key={t} className="flex gap-3 text-[15px] text-[#DCCFC3]"><Check className="w-4 h-4 mt-1 shrink-0" style={{ color: accent }} />{t}</li>
          ))}
        </ul>
        <div className="mt-auto pt-10">
          <p data-testid={`apps-price-${app.key}`} className="font-display text-4xl">{PRICE.replace(" AUD", "")} <span className="text-lg text-[#D9B47A]">AUD</span> <span className="text-base text-[#A8998C]">· lifetime</span></p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            {owned ? (
              <a href={app.base} data-testid={`apps-open-${app.key}`} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#D9B47A] text-[#0A0706] px-7 py-4 font-tt-mono text-[12px] uppercase tracking-[0.2em] hover:bg-[#F2E8DF] transition-colors">Open {app.short} <ArrowUpRight className="w-4 h-4" /></a>
            ) : (
              <Link to={`/initiate?product=${app.key}`} data-testid={`apps-buy-${app.key}`} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C2414F] text-[#F2E8DF] px-7 py-4 font-tt-mono text-[12px] uppercase tracking-[0.2em] hover:bg-[#D9B47A] hover:text-[#0A0706] transition-colors"><Lock className="w-4 h-4" /> Unlock for life</Link>
            )}
            {!owned && <a href={app.base} data-testid={`apps-signin-${app.key}`} className="inline-flex items-center justify-center rounded-full border border-[#D9B47A]/40 px-7 py-4 font-tt-mono text-[12px] uppercase tracking-[0.2em] text-[#D9B47A] hover:bg-[#D9B47A] hover:text-[#0A0706] transition-colors">Already yours? Open</a>}
          </div>
        </div>
      </article>
    </Reveal>
  );
};

const STEPS = [
  [Lock, "Unlock the app", "$297 AUD per app, or $497 AUD for both. One account works for both apps."],
  [Share, "Open it on your phone", "Visit the app in Safari on iPhone, or Chrome on Android, and sign in."],
  [PlusSquare, "Add to Home Screen", "Its own name and icon appear beside your other apps, protected by your 4-digit passcode."],
];

export default function Apps() {
  return (
    <main data-testid="apps-page" className="max-w-[1440px] mx-auto px-5 sm:px-10 pt-32 sm:pt-40 pb-28">
      <Eyebrow n="01">The Apps</Eyebrow>
      <h1 className="mt-8 font-display text-[clamp(2.75rem,7vw,6.5rem)] leading-[0.95] max-w-5xl">
        {["Two temples,", "in your pocket."].map((w, k) => (
          <span key={w} className="block pb-[0.06em]">
            <motion.span className={`block ${k ? "italic text-[#D9B47A]" : ""}`} initial={{ y: 28, opacity: 0.35 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 1.2, delay: 0.1 + k * 0.12, ease }}>{w}</motion.span>
          </span>
        ))}
      </h1>
      <p className="mt-8 max-w-2xl text-lg text-[#CFC2B6] leading-relaxed">Each practice lives in its own private app, installed from this website straight to your home screen. Choose one, or walk both paths.</p>
      <div className="mt-16 grid lg:grid-cols-2 gap-6">
        {Object.values(APPS).map((app, i) => <AppCard key={app.key} app={app} i={i} />)}
      </div>
      <BundleBand />
      <section className="mt-28 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <Eyebrow n="02">How to install</Eyebrow>
          <p className="mt-6 font-display text-3xl sm:text-4xl leading-tight">No app store. <em className="text-[#D9B47A]">No trace</em> but the icon.</p>
        </div>
        <ol className="lg:col-span-8 grid sm:grid-cols-3 gap-px bg-[#D9B47A]/15 border border-[#D9B47A]/15">
          {STEPS.map(([Icon, t, s], k) => (
            <li key={t} data-testid={`apps-step-${k + 1}`} className="bg-[#0A0706] p-7">
              <span className="font-tt-mono text-[12px] text-[#A8998C]">{String(k + 1).padStart(2, "0")}</span>
              <Icon className="mt-6 w-5 h-5 text-[#D9B47A]" strokeWidth={1.5} />
              <p className="mt-4 font-display text-2xl">{t}</p>
              <p className="mt-2 text-[15px] text-[#A8998C] leading-relaxed">{s}</p>
            </li>
          ))}
        </ol>
      </section>
    </main>
  );
}
